import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  SyndicateInviteQueryTypeEnumType,
  SyndicateInviteSourceTypeEnumType,
  SyndicateInviteType,
  SyndicateInviteTypeEnumType,
  SyndicateTypeEnumType,
} from '__generated__/graphql';
import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';

import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import debounce from 'utils/debounce';
import { getDeterministicColor } from 'utils/random-colors';

import CoSyndicateApplicableUserSlideOver, {
  CoSyndicateApplicableUser,
} from './CoSyndicateApplicableUserSlideOver';
import InviteUsersToSyndicateDialogButton from './InviteUsersToSyndicateDialogButton';
import RescindSyndicateUserInviteDialog from './RescindSyndicateUserInviteDialog';

const SYNDICATE_INVITES_QUERY = gql(`
  query SyndicateInvites($id: ID!, $cursor: ID, $filters: SyndicateInvitesFilterType, $coSyndicateApplicableUsersFilters: CoSyndicateApplicableUsersFilterType) {
    syndicate(id: $id) {
      id
      type
      invites(cursor: $cursor, filters: $filters) {
        nodes {
          id
          name
          email
          user {
            id
            name
            email
          }
          status
          createdAt
          emailStatus
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
      coSyndicateApplicableUsers(filters: $coSyndicateApplicableUsersFilters) {
        user {
          id
          name
          image {
            url
            name
          }
          email
          linkedInProfileUrl
          createdAt
          statistics {
            numberOfSyndicatesJoined
            totalInvestedAmount
            numberOfInvestments
            investRate
          }
        }
        syndicateUsers {
          id
          createdAt
          statistics {
            totalInvestedAmount
            numberOfInvestments
            investRate
          }
          syndicate {
            id
            name
            image
          }
        }
      }
    }
  }
`);

const SyndicateInvites: FC = () => {
  const { syndicateId } = useParams<{ syndicateId: string }>() as { syndicateId: string };
  const [inviteOnDisplay, setInviteOnDisplay] = useState<SyndicateInviteType | null>(null);
  const [selectedCoSyndicateUserIds, setSelectedCoSyndicateUserIds] = useState<string[]>([]);
  const [coSyndicateApplicableUserOnDisplay, setCoSyndicateApplicableUserOnDisplay] =
    useState<CoSyndicateApplicableUser | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const { loading, error, data, refetch, variables, fetchMore, networkStatus } = useQuery(
    SYNDICATE_INVITES_QUERY,
    {
      variables: {
        id: syndicateId,
        filters: {
          type: SyndicateInviteTypeEnumType.FromSyndicateToUser,
          status: SyndicateInviteQueryTypeEnumType.Pending,
          source: SyndicateInviteSourceTypeEnumType.Email,
        },
        coSyndicateApplicableUsersFilters: {
          syndicateId: undefined,
          text: undefined,
        },
      },
    }
  );

  const coSyndicateApplicableUsers = data?.syndicate.coSyndicateApplicableUsers;

  const uniqueCoSyndicatesWithColors = useMemo(() => {
    if (!coSyndicateApplicableUsers) return [];
    const allSyndicates = coSyndicateApplicableUsers.flatMap(
      coSyndicate =>
        coSyndicate?.syndicateUsers?.map(syndicateUser => syndicateUser.syndicate) || []
    );
    const uniqueSyndicatesMap = new Map();
    allSyndicates.forEach((syndicate, index) => {
      if (syndicate && syndicate.id && !uniqueSyndicatesMap.has(syndicate.id)) {
        uniqueSyndicatesMap.set(syndicate.id, {
          ...syndicate,
          color: getDeterministicColor(index.toString()),
        });
      }
    });
    return Array.from(uniqueSyndicatesMap.values());
  }, [coSyndicateApplicableUsers]);

  if (loading || !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const { nodes: invites, pageInfo } = data.syndicate.invites;

  const combinedInvites = invites.map(invite => ({
    ...invite,
    name: invite.user?.name || invite.name,
    email: invite.user?.email || invite.email,
  }));

  const filters = [
    {
      type: 'syndicateId',
      label: 'Syndicate',
      options: [
        {
          label: 'All',
          value: undefined,
        },
        ...uniqueCoSyndicatesWithColors.map(syndicate => ({
          label: syndicate.name,
          value: syndicate.id,
        })),
      ],
    },
  ];

  return (
    <>
      {inviteOnDisplay && (
        <RescindSyndicateUserInviteDialog
          id={inviteOnDisplay.id}
          onClose={() => setInviteOnDisplay(null)}
        />
      )}
      <CoSyndicateApplicableUserSlideOver
        open={!!coSyndicateApplicableUserOnDisplay}
        coSyndicateApplicableUser={coSyndicateApplicableUserOnDisplay}
        onClose={() => setCoSyndicateApplicableUserOnDisplay(null)}
      />
      <div className="mt-2 sm:flex sm:items-center sm:justify-between">
        <div className="max-w-xl">
          <h3 className="text-lg font-semibold leading-6 text-gray-700">Invite by email</h3>
          <p className="mt-1 text-md text-gray-500">
            Build your network by inviting investors to your syndicate.
          </p>
        </div>
        <InviteUsersToSyndicateDialogButton
          syndicateId={syndicateId}
          emailIdsToInvite={selectedCoSyndicateUserIds}
          showDialog={showInviteDialog}
          setShowDialog={setShowInviteDialog}
        />
      </div>
      <DataTable
        data={combinedInvites}
        onClick={invite => setInviteOnDisplay(invite)}
        columns={[
          {
            label: 'Invited Email',
            fieldName: 'email',
          },
          {
            label: 'Invited At',
            fieldName: 'createdAt',
            type: 'DATETIME',
          },
          {
            label: 'Status',
            fieldName: 'emailStatus',
            type: 'EMAIL_STATUS',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        paginationLoading={loading}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
      />

      {data.syndicate.type === SyndicateTypeEnumType.CoSyndicate && (
        <>
          <div className="mt-2 sm:flex sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <h3 className="text-lg font-semibold leading-6 text-gray-700">
                Invite your previous network
              </h3>
              <p className="mt-1 text-md text-gray-500">
                Bring back your previous investors to this syndicate.
              </p>
            </div>
            <InviteUsersToSyndicateDialogButton
              syndicateId={syndicateId}
              emailIdsToInvite={selectedCoSyndicateUserIds}
              showDialog={showInviteDialog}
              setShowDialog={setShowInviteDialog}
            />
          </div>
          <FilterPanel
            activeFilters={variables?.coSyndicateApplicableUsersFilters}
            filters={filters}
            refetch={refetch}
            filterKey="coSyndicateApplicableUsersFilters"
          />
          <TextField
            placeholder="Search for an investor"
            onChange={debounce(e => {
              refetch({
                coSyndicateApplicableUsersFilters: {
                  ...variables?.coSyndicateApplicableUsersFilters,
                  text: e.target.value,
                },
              });
            }, 500)}
          />
          <DataTable
            data={coSyndicateApplicableUsers || []}
            onClick={user => setCoSyndicateApplicableUserOnDisplay(user)}
            onSelect={(userIds: string[]) => {
              setSelectedCoSyndicateUserIds(userIds);
            }}
            columns={[
              {
                label: 'id',
                fieldName: 'user.email',
                type: 'SELECT',
              },
              {
                label: 'Name',
                fieldName: 'user.name',
              },
              {
                label: 'Email',
                fieldName: 'user.email',
              },
              {
                label: 'Associations',
                fieldName: 'syndicateUsers',
                type: 'CUSTOM',
                customValue: (value: any) => {
                  return (
                    <div className="flex flex-col gap-2">
                      {value.map((syndicateUser: any) => (
                        <Badge
                          key={syndicateUser.syndicate.id}
                          label={syndicateUser.syndicate.name}
                          color={
                            uniqueCoSyndicatesWithColors?.find(
                              s => s?.id === syndicateUser.syndicate.id
                            )?.color
                          }
                        />
                      ))}
                    </div>
                  );
                },
              },
            ]}
            filterLoading={networkStatus === NetworkStatus.setVariables}
          />
        </>
      )}
      <div className="mb-24"></div>
    </>
  );
};

export default SyndicateInvites;
